import { FieldDefinition, FormNode, MessageModel, readMessageModelByCode, Severity } from '@msdk/definitions'

import { isMessageVisible } from '../../utils/validation-visibility'

export enum ValidationCategory {
  Error = 'error',
  Warning = 'warning',
}

export interface ValidationTabTarget {
  groupId: string
  tabId: string
}

export interface ValidationIssue {
  category: ValidationCategory
  code: string
  fieldDef: FieldDefinition
  id: string
  node: FormNode
  rowNumber?: number
  tabPath: ValidationTabTarget[]
  targetId: string
}

type MessageResolver = (code: string) => MessageModel | undefined

function getCategory(message: MessageModel): ValidationCategory | undefined {
  if (message.type === Severity.Mandatory || message.type === Severity.Error) {
    return ValidationCategory.Error
  }

  if (message.type === Severity.Warning) {
    return ValidationCategory.Warning
  }
}

export function getValidationTarget(node: FormNode): string {
  return [node.value.entityId, node.value.listName, node.value.rowId, node.id].filter(Boolean).join(':')
}

export function getValidationIssues(
  root: FormNode,
  resolveMessage: MessageResolver = readMessageModelByCode,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const addIssues = (node: FormNode, tabPath: ValidationTabTarget[], rowNumber?: number) => {
    const fieldDef = node.value.fieldDefinition
    if (!fieldDef) {
      return
    }

    const targetId = getValidationTarget(node)
    const codes = Array.from(new Set(node.value.validationMessages))

    codes.forEach((code) => {
      const message = resolveMessage(code)
      if (!message || !isMessageVisible(message, node)) {
        return
      }

      const category = getCategory(message)
      if (!category) {
        return
      }

      issues.push({
        category,
        code,
        fieldDef,
        id: `${targetId}:${code}`,
        node,
        rowNumber,
        tabPath,
        targetId,
      })
    })
  }

  function visit(start: FormNode | undefined, tabPath: ValidationTabTarget[], rowNumber?: number): void {
    let node = start

    while (node) {
      if (node.value.type === 'Tab' || node.value.type === 'CustomTab') {
        const groupId = node.id
        let tab: FormNode | undefined = node

        while (tab && (tab.value.type === 'Tab' || tab.value.type === 'CustomTab')) {
          if (tab.value.visibility === 'Visible') {
            visit(tab.firstChild, [...tabPath, { groupId, tabId: tab.id }], rowNumber)
          }
          tab = tab.nextSibling
        }

        node = tab
        continue
      }

      addIssues(node, tabPath, rowNumber)

      if (node.value.type === 'List') {
        let row = node.firstChild
        let currentRow = 1

        while (row) {
          visit(row.firstChild, tabPath, currentRow)
          row = row.nextSibling
          currentRow += 1
        }
      } else {
        visit(node.firstChild, tabPath, rowNumber)
      }

      node = node.nextSibling
    }
  }

  visit(root, [])

  return issues
}
